var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import sortBy from 'lodash/sortBy';
import moment from 'moment-timezone';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, } from 'react';
import { useEffectOnce, useSetState } from 'react-use';
import { v4 as uuid } from 'uuid';
import { ajaxSend } from 'api/api';
import { flowsApi } from 'apis/flows';
import { uploadsApi } from 'apis/uploads';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { getDateAsUTC } from 'utils/date';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppPathParams } from 'utils/url/useAppPathParams';
export var ListItemType;
(function (ListItemType) {
    ListItemType["Standard"] = "Standard";
    ListItemType["Exception"] = "Exception";
})(ListItemType || (ListItemType = {}));
const initialState = {
    activeView: {
        view: 'edit',
        tab: 'businessHours',
        menu: 'hours',
    },
    autoReply: null,
    conflicts: {},
    listItems: {
        [ListItemType.Standard]: [],
        [ListItemType.Exception]: [],
    },
    modifiedListItems: {
        [ListItemType.Standard]: {},
        [ListItemType.Exception]: {},
    },
};
export const ScheduleContext = createContext(null);
function sortOpeningHourItems(openingHours, filter) {
    return filter === 'exception'
        ? sortBy(openingHours.filter(({ opening_hour_type }) => opening_hour_type !== 'regular'), ['next_date'])
        : sortBy(openingHours.filter(({ opening_hour_type }) => opening_hour_type === 'regular'), [({ recurring }) => recurring === null || recurring === void 0 ? void 0 : recurring.day_of_week]);
}
function getConflicts(exceptions) {
    return exceptions.reduce((conflicts, exception) => {
        // Check if the exception has the same next_date as any other one.
        //
        // If the opening hour item already has an `overridden_by` property declared, and
        // the `date` is the same as the inspected exception's `next_date`, then the
        // item is overridden already, so this will be evaludated `false`.
        const getHasConflict = () => {
            var _a;
            const hasOverriddenBy = !!((_a = exception.overridden_by) === null || _a === void 0 ? void 0 : _a.id);
            return exceptions.some(({ id, overridden_by, next_date }) => {
                var _a, _b;
                const isOverlap = exception.id !== id &&
                    moment.utc(exception.next_date).isSame(moment.utc(next_date), 'day');
                if (!isOverlap)
                    return false;
                if (hasOverriddenBy) {
                    const isOverriddenBy = ((_a = exception.overridden_by) === null || _a === void 0 ? void 0 : _a.id) === id;
                    const isOverridingActive = moment
                        .utc(next_date)
                        .isSame(moment.utc((_b = exception.overridden_by) === null || _b === void 0 ? void 0 : _b.date), 'day');
                    return isOverriddenBy && !isOverridingActive;
                }
                const isOverriding = exception.id === (overridden_by === null || overridden_by === void 0 ? void 0 : overridden_by.id);
                const isOverridingActive = moment
                    .utc(overridden_by === null || overridden_by === void 0 ? void 0 : overridden_by.date)
                    .isSame(moment.utc(exception.next_date), 'day');
                return !isOverriding || (isOverriding && !isOverridingActive);
            });
        };
        const hasConflict = getHasConflict();
        return Object.assign(Object.assign({}, conflicts), (hasConflict && {
            [exception.id]: exception,
        }));
    }, {});
}
export function ScheduleProvider({ children }) {
    var _a;
    const [{ activeView, autoReply, conflicts, listItems, modifiedListItems }, setState,] = useSetState(initialState);
    const [intervalsErrors, setIntervalsErrors] = useState({
        [ListItemType.Standard]: [],
        [ListItemType.Exception]: [],
    });
    const [templates, setTemplates] = useState(useAppSelector((state) => state.settings.templates.templatesList.filter(({ type }) => type === 'email_auto_reply')));
    const notify = useNotifications();
    const { data: announcementData, refetch: refetchAudioMessages } = uploadsApi.useReadAnnouncementsQuery();
    const [readHolidays, { data: holidaysResponse }] = flowsApi.useLazyReadFlowsSchedulesHolidaysQuery();
    const announcements = useMemo(() => {
        var _a;
        const uploads = (_a = announcementData === null || announcementData === void 0 ? void 0 : announcementData.data) === null || _a === void 0 ? void 0 : _a.uploads;
        return uploads
            ? uploads.map(({ url, pretty_name, created_by }) => ({
                url,
                author: (created_by === null || created_by === void 0 ? void 0 : created_by.name) || '',
                label: pretty_name,
            }))
            : null;
    }, [announcementData]);
    const { id } = useAppPathParams('/settings/businesshours/:id');
    const [readFlowSchedule, { data: scheduleData = null, isError }] = flowsApi.useLazyReadFlowScheduleQuery();
    const handleFetchSchedule = useCallback(() => {
        readFlowSchedule({ id });
    }, [id, readFlowSchedule]);
    useEffectOnce(() => {
        handleFetchSchedule();
        handleFetchTemplates();
    });
    const handleFetchHolidays = useCallback((countryCode, timezone) => {
        readHolidays({ country_code: countryCode, timezone });
    }, [readHolidays]);
    useEffect(() => {
        var _a;
        const { country_code, timezone } = scheduleData || {};
        const hasHolidays = !!((_a = holidaysResponse === null || holidaysResponse === void 0 ? void 0 : holidaysResponse.data) === null || _a === void 0 ? void 0 : _a.holidays.length);
        if (country_code && timezone && !hasHolidays) {
            handleFetchHolidays(country_code, timezone);
        }
    }, [holidaysResponse, scheduleData, handleFetchHolidays]);
    useEffect(() => {
        if (scheduleData) {
            const { opening_hours: openingHours } = scheduleData;
            const sortedExceptions = sortOpeningHourItems(openingHours, 'exception');
            setState({
                conflicts: getConflicts(sortedExceptions),
                autoReply: scheduleData.auto_reply_template_id,
                listItems: {
                    [ListItemType.Standard]: sortOpeningHourItems(openingHours, 'standard'),
                    [ListItemType.Exception]: sortedExceptions,
                },
            });
        }
    }, [scheduleData, setState]);
    const handleSetModifiedListItems = useCallback((type, modifiedItems) => {
        const initialItems = scheduleData === null || scheduleData === void 0 ? void 0 : scheduleData.opening_hours.reduce((acc, curr) => (Object.assign(Object.assign({}, acc), { [curr.id]: curr })), {});
        setState((prevState) => {
            const newListItems = [...prevState.listItems[type]];
            const newModifiedListItems = Object.assign({}, prevState.modifiedListItems[type]);
            // We have to keep track if an entry got a new id, in case of archiving.
            const newIds = {};
            Object.entries(modifiedItems).forEach(([id, modifiedItem]) => {
                var _a;
                const { start_date: startDate } = modifiedItem;
                const listItemIndex = newListItems.findIndex(({ id: itemId }) => itemId === id);
                const initialItem = initialItems === null || initialItems === void 0 ? void 0 : initialItems[id];
                const isSameDay = moment.utc(startDate).isSame(moment.utc(), 'day');
                const isPastDay = moment.utc().isAfter(startDate);
                const shouldCreateNewEntry = !isSameDay && isPastDay;
                // If item was deleted
                if (modifiedItem.archived) {
                    newListItems.splice(listItemIndex, 1);
                    if (initialItem) {
                        newModifiedListItems[id] = modifiedItem;
                        // If the archived (deleted) exception previously resolved a conflict,
                        // the overridden items' `overridden_by` property has to be removed,
                        // since it will be referring to a non-existent item.
                        newListItems.forEach((_a) => {
                            var { overridden_by } = _a, overriddenException = __rest(_a, ["overridden_by"]);
                            if ((overridden_by === null || overridden_by === void 0 ? void 0 : overridden_by.id) === id) {
                                const overriddenExceptionListItemIndex = newListItems.findIndex(({ id: itemId }) => itemId === overriddenException.id);
                                newModifiedListItems[overriddenException.id] =
                                    overriddenException;
                                newListItems[overriddenExceptionListItemIndex] =
                                    overriddenException;
                            }
                        });
                    }
                    else {
                        // If the item was created after the initial render,
                        // we don't send it to the backend. We remove it from
                        // the modified items object.
                        delete newModifiedListItems[id];
                    }
                }
                else {
                    // If item is already in the list
                    if (listItemIndex >= 0) {
                        if (shouldCreateNewEntry && initialItem) {
                            const newId = uuid();
                            newIds[id] = newId;
                            const { overridden_by } = modifiedItem;
                            // Check if the `overridden_by.id` is part of the newIds,
                            // thus we know if the id should be updated, because it's pointing
                            // to an archived entry.
                            const shouldOverriddenByIdBeUpdated = overridden_by && newIds[overridden_by.id];
                            const newItem = Object.assign(Object.assign(Object.assign({}, modifiedItem), { id: newId }), (shouldOverriddenByIdBeUpdated && {
                                overridden_by: Object.assign(Object.assign({}, overridden_by), { id: newIds[overridden_by.id] }),
                            }));
                            newModifiedListItems[initialItem.id] = Object.assign(Object.assign({}, initialItem), { archived: moment().toISOString() });
                            if (type === ListItemType.Standard) {
                                const dayOfWeek = (_a = newListItems[listItemIndex].recurring) === null || _a === void 0 ? void 0 : _a.day_of_week;
                                const startDate = moment()
                                    .isoWeekday(dayOfWeek)
                                    .add(1, 'week');
                                newItem.start_date = getDateAsUTC(startDate).toISOString();
                            }
                            else {
                                // If the exception resolved a conflict, the id in the overridden_by
                                // property of the overridden item should be updated as well.
                                newListItems.forEach((newListItem) => {
                                    const { id: listItemId, overridden_by } = newListItem;
                                    if ((overridden_by === null || overridden_by === void 0 ? void 0 : overridden_by.id) === modifiedItem.id) {
                                        const overriddenItemIndex = newListItems.findIndex(({ id: itemId }) => itemId === listItemId);
                                        const newOverriddenItem = Object.assign(Object.assign({}, newListItem), { overridden_by: Object.assign(Object.assign({}, overridden_by), { id: newId }) });
                                        newModifiedListItems[listItemId] = newOverriddenItem;
                                        newListItems[overriddenItemIndex] = newOverriddenItem;
                                    }
                                });
                            }
                            newListItems[listItemIndex] = newItem;
                            newModifiedListItems[newId] = newItem;
                        }
                        else {
                            // If modified, update the list
                            newListItems[listItemIndex] = modifiedItem;
                            newModifiedListItems[id] = modifiedItem;
                        }
                    }
                    else {
                        // If new item, add it to the list
                        newListItems.push(modifiedItem);
                        newModifiedListItems[id] = modifiedItem;
                    }
                }
            });
            return Object.assign({ listItems: Object.assign(Object.assign({}, prevState.listItems), { [type]: type === ListItemType.Standard
                        ? sortOpeningHourItems(newListItems, 'standard')
                        : sortOpeningHourItems(newListItems, 'exception') }), modifiedListItems: Object.assign(Object.assign({}, prevState.modifiedListItems), { [type]: newModifiedListItems }) }, (type === ListItemType.Exception && {
                conflicts: getConflicts(newListItems),
            }));
        });
    }, [scheduleData, setState]);
    const handleSetErrors = useCallback((type, errors) => void setIntervalsErrors((prevState) => (Object.assign(Object.assign({}, prevState), { [type]: errors }))), []);
    const handleFetchTemplates = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        try {
            const { data: { templates: templatesList }, } = yield ajaxSend({
                path: '/v1/templating/templates',
            });
            setTemplates(templatesList.filter(({ type }) => type === 'email_auto_reply'));
        }
        catch (error) {
            console.warn(error);
            notify({
                title: 'An error occurred when trying to fetch templates.',
            }, NotificationType.Error);
        }
    }), [notify]);
    return (_jsx(ScheduleContext.Provider, Object.assign({ value: {
            activeView,
            autoReply,
            conflicts,
            holidays: ((_a = holidaysResponse === null || holidaysResponse === void 0 ? void 0 : holidaysResponse.data) === null || _a === void 0 ? void 0 : _a.holidays) || [],
            initialSchedule: scheduleData,
            intervalsErrors,
            listItems,
            meta: {
                isError,
            },
            modifiedListItems,
            announcements,
            templates,
            fetchTemplates: handleFetchTemplates,
            getHolidays: handleFetchHolidays,
            refetchSchedules: handleFetchSchedule,
            refetchAudioMessages,
            setActiveView: (activeView) => setState({ activeView }),
            setAutoReply: (autoReply) => setState({ autoReply }),
            setInitialState: () => setState({ modifiedListItems: initialState.modifiedListItems }),
            setIntervalsErrors: handleSetErrors,
            setModifiedListItems: handleSetModifiedListItems,
        } }, { children: children })));
}
/** A hook exposing the context functionality. */
export const useScheduleContext = () => {
    const context = useContext(ScheduleContext);
    if (!context) {
        throw new Error('Missing ScheduleContext');
    }
    return context;
};
